import React, {
	useMemo,
	useCallback,
	useEffect,
	useContext,
	useLayoutEffect,
} from 'react';
import {
	ScriptTagContext,
	LoggerContext,
	PreviewContext,
	useVisitTrigger,
	TrackError,
	useSendApiLogs,
	isRATAvailable,
} from '@rmc/webcx-web-libs';
import { useAntiFlickering } from '../../hooks/useAntiFlickering';
import { useGetValidCampaigns } from '../../hooks/useGetValidCampaigns';
import { CampaignsContext } from '../../context/CampaignsContext';
import {
	WebBlocksApiNormalBlockT,
	WebBlocksApiBlockWithCorrelationIdT,
} from '../../lib/REST/types';
import { BlocksReplacer } from '../BlocksReplacer';
import { getMainAppInfo, filterDuplicates } from './utils';
import {
	API_KEY_EVENT_LOGGER,
	API_URL_EVENT_LOGGER,
	RAT_PROJECT_KEY,
	VERSION,
	REDIRECT_HOST,
} from '../../config';
import { useEventsApiPostEvent } from '../../api/v3/web/events/post';
import { TrackAppHit } from '../../lib/RAT';
import { EventType } from '../../types/EventType';
import { Script } from '../AppUserInterface/Script';

type EventPropsType = {
	campaignId: string;
	contentId: string;
	correlationId?: string | null;
	replacementId?: string;
	impressionId?: string;
	selector?: string | null;
};

export function MainApp() {
	const scriptData = useContext(ScriptTagContext);
	const previewQuery: { rcxpv?: string } = useContext(PreviewContext);
	const { updateGlobalLog } = useContext(LoggerContext);
	const {
		campaignIdCorrelationInfo,
		isLoading: cdnLoading,
		error: campaignIdsError,
	} = useContext(CampaignsContext);
	const { sessionId, device, userId, isMobile, domainScopedSessionId } =
		getMainAppInfo();
	const {
		clientId,
		ratAccountId,
		ratServiceType,
		ratServiceId,
		backupSdkSrc,
		ratApiAccountSrc,
	} = scriptData;

	const isPreview = Object.keys(previewQuery).length !== 0;
	const pageUrl = window?.location?.href || '';
	const campaignIds = useMemo(
		() => campaignIdCorrelationInfo.map((info) => info.campaignId),
		[campaignIdCorrelationInfo]
	);
	const campaignsPayload = useVisitTrigger({
		cdnCampaignIds: campaignIds,
		isLoading: cdnLoading,
		isVisTrigDisabled: isPreview,
	});

	const { campaigns, initialCampaigns, invalidCampaigns, error, isLoading } =
		useGetValidCampaigns({
			device,
			userId,
			sessionId,
			domainScopedSessionId,
			previewQuery,
			onLog: updateGlobalLog,
			campaigns: campaignsPayload,
			clientId,
		});

	const campaignsWithCorrelationId: WebBlocksApiBlockWithCorrelationIdT[] =
		useMemo(
			() =>
				campaigns.map((campaign) => {
					const correlationId = campaignIdCorrelationInfo.find(
						(info) => info.campaignId === campaign.campaignId
					)?.correlationId;

					return {
						...campaign,
						correlationId,
					};
				}),
			[campaigns, campaignIdCorrelationInfo]
		);

	const { onBlockLoad } = useAntiFlickering(
		campaignsWithCorrelationId,
		scriptData,
		Boolean(error)
	);

	useEffect(() => {
		const invalidNormalBlocks = invalidCampaigns.filter((campaign) =>
			campaign.replacements?.filter?.((replacement) => replacement.selector)
		) as WebBlocksApiNormalBlockT[];

		invalidNormalBlocks.forEach((invalidBlock) => {
			invalidBlock.replacements.forEach((replacement) => {
				onBlockLoad(replacement.selector);
			});
		});
	}, [invalidCampaigns]);

	useLayoutEffect(() => {
		if (
			!isRATAvailable() &&
			!ratApiAccountSrc &&
			!window?.location?.href.includes('localhost')
		) {
			TrackError({
				action: 'ERROR',
				error: 'RAT_TRACKING_UNAVAILABLE',
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'TRACKING',
				isMobile,
				userId,
				sessionId,
				clientId,
				pageUrl,
				eventLoggerUrl: API_URL_EVENT_LOGGER,
				eventLoggerApiKey: API_KEY_EVENT_LOGGER,
			});
		} else if (window?.location?.href.includes('localhost')) {
			/* eslint-disable no-console */
			console.log('RAT_TRACKING_UNAVAILABLE', {
				action: 'ERROR',
				error: 'RAT_TRACKING_UNAVAILABLE',
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'TRACKING',
				isMobile,
				userId,
				sessionId,
				clientId,
				pageUrl,
			});
		}
	}, []);

	const postApiEvent = useEventsApiPostEvent<EventType>({
		clientId,
		sessionId,
		domainScopedSessionId,
		previewQuery,
		onError: (
			err: unknown,
			variables: {
				eventType: EventType;
				campaignId: string;
				contentId: string;
				url?: string;
				replacementId?: string;
				selector?: string | null;
				impressionId?: string;
			}
		) => {
			const {
				eventType,
				contentId,
				campaignId,
				selector,
				replacementId,
				impressionId,
			} = variables;
			// NOTE: correlationId is tracked for debugging purposes
			// It's added from top level context since it's not present in the API
			const correlationId = campaignIdCorrelationInfo.find(
				(info) => info.campaignId === campaignId
			)?.correlationId;

			return TrackError({
				action: `${eventType.toUpperCase()}_ERROR`,
				error: err,
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				screen: 'METRIC',
				campaignId,
				correlationId:
					typeof correlationId === 'string' ? correlationId : undefined,
				contentId,
				replacementId,
				selector,
				impressionId,
				isMobile,
				userId,
				sessionId,
				domainScopedSessionId,
				clientId,
				pageUrl,
				eventLoggerUrl: API_URL_EVENT_LOGGER,
				eventLoggerApiKey: API_KEY_EVENT_LOGGER,
				isPreview,
			});
		},
	});

	const postHitEvent = ({
		campaignId,
		contentId,
		replacementId,
		impressionId,
		selector,
	}: EventPropsType) => {
		const obj = {
			eventType: EventType.HIT,
			campaignId,
			contentId,
			replacementId,
			impressionId,
			selector,
		};
		postApiEvent(obj);
	};

	const postAppearEvent = ({
		campaignId,
		contentId,
		replacementId,
		impressionId,
		selector,
	}: EventPropsType) =>
		postApiEvent({
			eventType: EventType.VIEW,
			campaignId,
			contentId,
			replacementId,
			impressionId,
			selector,
		});

	const onAppear = useCallback(
		({
			campaignId,
			contentId,
			replacementId,
			impressionId,
			selector,
		}: EventPropsType) =>
			postAppearEvent({
				campaignId,
				contentId,
				replacementId,
				impressionId,
				selector,
			}),
		[
			campaigns,
			ratAccountId,
			ratServiceType,
			ratServiceId,
			isMobile,
			previewQuery,
			postAppearEvent,
		]
	);

	const onRendered = useCallback(
		({
			campaignId,
			contentId,
			replacementId,
			impressionId,
			correlationId,
			selector,
		}: EventPropsType) => {
			if (selector) {
				onBlockLoad(selector);
			}

			return TrackAppHit({
				version: VERSION,
				projectKey: RAT_PROJECT_KEY,
				campaignId,
				contentId,
				ratAccountId,
				ratServiceType,
				ratServiceId,
				isMobile,
				userId,
				sessionId,
				domainScopedSessionId,
				clientId,
				correlationId,
				replacementId,
				selector,
				impressionId,
				ratApiAccountSrc,
				isPreview,
				trackCb: () =>
					postHitEvent({
						campaignId,
						contentId,
						replacementId,
						impressionId,
						selector,
					}),
			});
		},
		[
			campaigns,
			ratAccountId,
			ratServiceType,
			ratServiceId,
			isMobile,
			postHitEvent,
			previewQuery,
			onBlockLoad,
		]
	);

	useSendApiLogs({
		screen: 'MESSAGE_API',
		version: VERSION,
		projectKey: RAT_PROJECT_KEY,
		apiError: error,
		apiIsLoading: isLoading,
		apiItems: initialCampaigns,
		apiItemsInvalid: invalidCampaigns,
		isMobile,
		userId,
		sessionId,
		domainScopedSessionId,
		clientId,
		pageUrl,
		eventLoggerUrl: API_URL_EVENT_LOGGER,
		eventLoggerApiKey: API_KEY_EVENT_LOGGER,
		isPreview,
	});

	if ((campaignIdsError || error) && backupSdkSrc) {
		const body = document.body || document.getElementsByTagName('body')[0];
		return <Script url={backupSdkSrc} container={body} />;
	}

	if (!campaigns.length) {
		return null;
	}

	const blocks = filterDuplicates({ blocks: campaignsWithCorrelationId });

	return (
		<BlocksReplacer
			blocks={blocks}
			clickLinkData={{
				redirectHost: REDIRECT_HOST,
				clientId,
				version: VERSION,
				sessionId,
				screen: 'WB',
				...(previewQuery?.rcxpv ? { rcxpv: previewQuery?.rcxpv } : {}),
			}}
			onAppear={onAppear}
			onRendered={onRendered}
		/>
	);
}
